import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalePipe } from '../../../locale.pipe';
import { Patient } from 'src/app/models/patient.model';
import { Address, Coordinates } from 'src/app/models/address.model';
import { PatientService } from 'src/app/services/patient.service';
import { ToastService } from 'src/app/services/toast.service';
import { CountryService } from 'src/app/services/country.service';
import { Country } from 'src/app/models/country.model';
import { Provider } from 'src/app/models/provider.model';
import { UserService } from 'src/app/services/user.service';
import { GeocodingService } from 'src/app/services/geocoding.service';
import { GeocodingSuggestion } from 'src/app/models/geocoding.model';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { normalizedSearch, valueChangesPipe } from 'src/app/utils/value-changes-pipe.function';
import { displayItemName } from 'src/app/utils/display-fields.utils';
import { MessageNavigationService } from 'src/app/services/message-navigation.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: false
})
export class AddressFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly patientService = inject(PatientService);
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly localePipe = inject(LocalePipe);
  private readonly countryService = inject(CountryService);
  private readonly geoService = inject(GeocodingService);
  private readonly messageNavigationService = inject(MessageNavigationService);

  public showMap = false;
  public patientId = '';
  public coordinates: Coordinates | null = null;
  public countries: Country[] = [];
  public patient: Patient | null = null;
  public provider: Provider | null = null;
  public isRedGlobal = false;
  public isCurrentLocation = false;
  public isLoading = true;
  public filteredAddresses: Observable<GeocodingSuggestion[]> = of([]);
  public filteredCountries: Observable<Country[]> = of([]);
  public addressForm!: FormGroup;

  async ngOnInit(): Promise<void> {
    this.getPatientId();
    try {
      await Promise.all([
        this.loadCountries(),
        this.loadPatient()
      ]);
    
      this.buildForm();

      const providerCountry: Country | undefined = this.patient?.provider?.country;
      if (this.patient && !this.isRedGlobal && providerCountry?.id) {
        this.country?.setValue(this.getCountry());
        this.country?.disable();
      } else {
        this.country?.setValidators([Validators.required]);
        this.country?.updateValueAndValidity();
      }

      this.setupCountryFiltering();
      this.setupAddressFiltering();
      this.isLoading = false;
    } catch (error) {
      this.isLoading = true;
    }
  }

  private buildForm(): void {
    this.addressForm = this.fb.group({
      country: [''],
      address: ['', Validators.required],
      zipCode: ['', Validators.required]
    });

    this.country?.valueChanges.subscribe(() => {
      this.zipCode?.setValidators(this.showZipCode ? [Validators.required] : []);
      if (!this.showZipCode) this.zipCode?.setValue('');
      this.zipCode?.updateValueAndValidity();
    });
  }

  private setupCountryFiltering(): void {
    if (!this.country) return;
    this.filteredCountries = valueChangesPipe(this.country.valueChanges).pipe(
      map((value) => {
        return this.filterCountries(value);
      })
    );
  }

  private setupAddressFiltering(): void {
    this.address?.valueChanges
      .pipe(
        normalizedSearch(300, 2),
        filter((value) => {
          if (!value) {
            this.filteredAddresses = of([]);
          }
          return !!value;
        })
      )
      .subscribe((value) => {
        this.searchLocation(value);
      });
  }

  private filterCountries(searchValue: string): Country[] {
    const filterValue = searchValue.toLowerCase().trim();
    if (!filterValue) {
      return this.countries;
    }
    return this.countries.filter((country) => country.name.toLowerCase().includes(filterValue));
  }

  private async loadPatient(): Promise<void> {
    try {
      this.patient = await this.userService.getMe<Patient>();
      this.isRedGlobal = !!this.patient.provider?.isRedGlobal;
    } catch (error) {
      this.messageNavigationService.goToMessage(this.localePipe.transform('address_form_token_expired'));
      throw error;
    }
  }

  private async loadCountries(): Promise<void> {
    try {
      this.countries = await this.countryService.get();
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) return;
        const message = error instanceof HttpErrorResponse ? error.error?.message : undefined;
        this.toastService.openErrorToast(message);
      }
    }
  }

  private getPatientId() {
    this.patientId = this.route.snapshot.queryParams['patient'];
  }

  getCurrentLocation() {
    this.addressForm.patchValue({
      address: '',
      zipCode: '',
      ...(this.isRedGlobal && { country: '' })
    });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.showMap = true;

          this.geoService.reverseGeocode(this.coordinates.lat, this.coordinates.lng).subscribe({
            next: (result) => {
              if (result) {
                const selectedCountry = this.getCountry(result.countryCode);
                this.country?.setValue(selectedCountry);
                this.address?.patchValue({ address: result.address });
                this.zipCode?.setValue(result.zipCode);
                this.filteredAddresses = of([result]);
              }
            },
            error: () => {
              this.toastService.openErrorToast('address_form_location_error');
            }
          });
        },
        () => {
          this.toastService.openErrorToast('address_form_location_error');
        }
      );
    } else {
      this.toastService.openErrorToast('address_form_geolocation_not_supported');
    }
  }

  onLocationSelected(coords: Coordinates) {
    this.coordinates = coords;
  }

  async onSubmit() {
    const { address: addressValue, zipCode } = this.addressForm.value;

    const addressData: Address = {};

    if (this.addressForm.valid) {
      addressData.address = addressValue?.address || null;
      addressData.zipCode = zipCode;
    }

    if (this.coordinates) {
      addressData.latitude = this.coordinates?.lat;
      addressData.longitude = this.coordinates?.lng;
    }

    try {
      await this.patientService.updatePatient(this.patientId, { address: addressData });
      this.messageNavigationService.goToMessage(this.localePipe.transform('address_form_success_message'));
    } catch (error) {
      const message = error instanceof HttpErrorResponse ? error.error?.message : undefined;
      this.toastService.openErrorToast(message);
    }
  }

  private searchLocation(address: string) {
    const isAddressEmpty = !address?.trim();
    const selectedCountry = this.country?.value;
    const isCountryEmpty = !selectedCountry;

    if ((!this.isRedGlobal && isAddressEmpty) || (this.isRedGlobal && (isCountryEmpty || isAddressEmpty))) {
      this.toastService.openErrorToast('address_form_location_enter_address_error');
      return;
    }

    const country = selectedCountry?.countryCode || '';

    this.geoService.getCoordinates(address, country).subscribe({
      next: (filteredAddresses: GeocodingSuggestion[]) => {
        this.filteredAddresses = of(filteredAddresses);
      },
      error: () => {
        this.toastService.openErrorToast('address_form_location_error');
      }
    });
  }

  onAddressSelected(suggestion: GeocodingSuggestion) {
    this.coordinates = suggestion?.coordinates;
    this.showMap = true;
  }

  displayAddressFn = (suggestion: GeocodingSuggestion): string => {
    return suggestion?.address || '';
  };

  displayCountryFn = displayItemName;

  private getCountry(countryCode?: string): Country | null {
    if (countryCode) return this.countries.find((c) => c.countryCode === countryCode) || null;

    const providerCountry = this.patient?.provider?.country;
    if (!providerCountry) return null;
    return this.countries.find((c) => c.id === providerCountry.id) || null;
  }

  get country() {
    return this.addressForm.get('country');
  }

  get address() {
    return this.addressForm.get('address');
  }

  get zipCode() {
    return this.addressForm.get('zipCode');
  }

  get showZipCode(): boolean {
    return this.geoService.usesZipCode(this.country?.value?.countryCode);
  }
}
